import React, { useEffect, useState } from "react";
import { fetchMyReferral } from "../lib/api";

/**
 * ReferralCard — shown on the Player Dashboard.
 *
 * States:
 *  - LOCKED (already cashed in once): show a small "REFERRAL PRO USED" stamp
 *    so the user knows the perk is one-time.
 *  - PRO_ACTIVE: show countdown (days left until referral PRO expires) + a
 *    "Keep sharing to grow your squad" CTA. Link still works.
 *  - DEFAULT: progress bar X/10 + share link + Copy + native share.
 */
export function ReferralCard({ onToast }) {
  const [state, setState] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchMyReferral().then((s) => {
      if (!cancelled) setState(s);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!state) {
    return (
      <div className="glass rounded-3xl p-5" data-testid="dash-referral-loading">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
          <i className="fas fa-link mr-1" /> REFERRALS
        </p>
        <p className="mt-2 text-[11px] text-white/40">Loading your link…</p>
      </div>
    );
  }

  const { link, count, pro_active, pro_expires_at, locked } = state;
  const daysLeft = pro_expires_at
    ? Math.max(0, Math.ceil((new Date(pro_expires_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      onToast?.("🔗 Referral link copied!");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      onToast?.("Couldn't copy. Long-press to copy manually.");
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "LEAGUE HUB",
          text: "Join my squad on LEAGUE HUB — neon esports leagues, free to start.",
          url: link,
        });
      } catch { /* user cancelled */ }
    } else {
      copy();
    }
  };

  return (
    <div
      data-testid="dash-referral-card"
      className={`glass rounded-3xl p-5 border-2 ${
        pro_active
          ? "border-[#14b8a6]/70 bg-[#14b8a6]/5 shadow-[0_0_20px_rgba(253,224,71,0.25)]"
          : locked
            ? "border-white/15"
            : "border-[#14b8a6]/40"
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
          <i className="fas fa-link mr-1" /> SHARE LEAGUE HUB
        </p>
        {pro_active && (
          <span
            data-testid="dash-referral-pro-active"
            className="text-[9px] tracking-widest font-bold px-2 py-0.5 rounded-md bg-[#14b8a6]/15 border border-[#14b8a6]/60 text-[#14b8a6]"
          >
            👑 PRO · {daysLeft}D LEFT
          </span>
        )}
        {locked && !pro_active && (
          <span
            data-testid="dash-referral-locked-chip"
            title="One-time referral perk already used"
            className="text-[9px] tracking-widest font-bold px-2 py-0.5 rounded-md bg-white/5 border border-white/20 text-white/50"
          >
            ✓ PERK USED
          </span>
        )}
      </div>

      <p className="text-[11px] text-white/70 leading-snug">
        Share your link to grow your squad. We'll keep a running tally of friends
        who join through you.
      </p>

      {/* Referral count */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-[10px] tracking-widest mb-1">
          <span className="text-[#a3a3ac]">FRIENDS REFERRED</span>
          <span className="text-white" data-testid="dash-referral-progress">
            <b className="text-[#14b8a6]">{count}</b>
          </span>
        </div>
      </div>

      {/* Share link */}
      <div className="mt-4 flex gap-2">
        <input
          readOnly
          value={link}
          data-testid="dash-referral-link-input"
          onFocus={(e) => e.target.select()}
          className="flex-1 min-w-0 bg-black/50 border border-white/15 rounded-xl px-3 py-2 text-[11px] text-[#a3a3ac] font-mono truncate"
        />
        <button
          type="button"
          onClick={copy}
          data-testid="dash-referral-copy-btn"
          className="text-[10px] tracking-widest px-3 py-2 rounded-xl btn-neon whitespace-nowrap"
        >
          {copied ? "✓ COPIED" : <><i className="fas fa-copy mr-1" />COPY</>}
        </button>
      </div>
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          onClick={share}
          data-testid="dash-referral-share-btn"
          className="flex-1 text-[10px] tracking-widest px-3 py-1.5 rounded-xl btn-ghost"
        >
          <i className="fas fa-share-nodes mr-1" />SHARE LINK
        </button>
        <a
          href={`https://twitter.com/intent/tweet?text=${encodeURIComponent("Drop into my squad on LEAGUE HUB — neon esports leagues. ")}${encodeURIComponent(link)}`}
          target="_blank"
          rel="noreferrer noopener"
          data-testid="dash-referral-tweet-btn"
          className="flex-1 text-center text-[10px] tracking-widest px-3 py-1.5 rounded-xl btn-ghost"
        >
          <i className="fab fa-x-twitter mr-1" />POST ON X
        </a>
      </div>

      {locked && pro_active && (
        <p className="mt-3 text-[10px] text-[#14b8a6]/70 leading-snug">
          You've already cashed in your one-time referral PRO month. Keep
          referring to help grow the realm — your link still works.
        </p>
      )}
      {locked && !pro_active && (
        <p className="mt-3 text-[10px] text-white/50 leading-snug">
          You've already used your one-time referral PRO month. Upgrade in
          <a className="text-[#14b8a6] mx-1" href="/pricing">PRICING</a>
          to keep PRO perks active.
        </p>
      )}
    </div>
  );
}

export default ReferralCard;
